import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Callout } from '@/components/ui';
import styles from './AppDetailConnectionTab.module.css';

export interface RevealedField {
    label: string;
    value: string;
    /** Long values (a 64-char HMAC secret) wrap instead of ellipsing. */
    wrap?: boolean;
}

interface AppCredentialRevealProps {
    fields: RevealedField[];
    /** The server's own warning text — rendered verbatim, never re-worded here. */
    warning: string;
    /** True when this replaced a credential that was already in use. */
    rotated: boolean;
    onCopy: (value: string, label: string) => void;
    onDismiss: () => void;
    /** Extra content under the warning — e.g. the rotation-overlap deadline. */
    children?: ReactNode;
}

/**
 * EAA-1810: the one-time reveal for a credential the server returns exactly once
 * — the OIDC `client_secret` and the HMAC `shared_secret` both land here.
 *
 * WHY IT IS ITS OWN COMPONENT: the Connection tab now reveals two different
 * credentials with identical rules, and `AppOnboardingModal` already reveals a
 * third. Hand-rolling the panel a second time inside the tab would have made
 * the "shown once, never refetched" discipline a convention repeated in three
 * places instead of a shape enforced in one.
 *
 * The value is passed in as a prop and lives ONLY in the caller's component
 * state — never in a TanStack Query cache, never re-read from the server. It
 * disappears for good when the caller dismisses or unmounts, which is why the
 * dismiss button is labelled around "I have saved it" rather than "close": the
 * operator is acknowledging a loss, not closing a dialog.
 */
export function AppCredentialReveal({
    fields,
    warning,
    rotated,
    onCopy,
    onDismiss,
    children,
}: AppCredentialRevealProps) {
    const { t } = useTranslation();

    return (
        <div className={styles.reveal} role="alert" data-testid="app-credential-reveal">
            <p className={styles.revealIntro}>
                {t('console.appDetail.connection.reveal.onceWarning')}
            </p>
            {fields.map((field) => (
                <div key={field.label} className={styles.codeField}>
                    <span className={styles.codeFieldLabel}>{field.label}</span>
                    <span className={styles.codeFieldBox}>
                        <code
                            className={
                                field.wrap
                                    ? `${styles.codeFieldValue} ${styles.codeFieldValueWrap}`
                                    : styles.codeFieldValue
                            }
                        >
                            {field.value}
                        </code>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => onCopy(field.value, field.label)}
                        >
                            {t('console.appDetail.connection.copyAction')}
                        </Button>
                    </span>
                </div>
            ))}
            <Callout
                tone={rotated ? 'warning' : 'danger'}
                label={t('console.appDetail.connection.reveal.lossLabel')}
                longLabel
            >
                {warning}
            </Callout>
            {children}
            <div className={styles.revealActions}>
                <Button type="primary" onClick={onDismiss}>
                    {t('console.appDetail.connection.reveal.savedButton')}
                </Button>
            </div>
        </div>
    );
}

export default AppCredentialReveal;
